import React from "react";
import PropTypes from "prop-types";
import { Details, Summary } from "react-accessible-details";

import { useAudioGuideApi } from "../../lib/hooks";

import css from "./Footer.module.scss";

/* eslint-disable react/forbid-prop-types */

const implodeAnd = (items = []) => {
  if (items.length === 0) {
    return "";
  }
  if (items.length === 1) {
    return items[0];
  }
  const last = items.pop();
  return `${items.join(", ")} and ${last}`;
};

const OpeningHoursTable = ({
  showDefault = false,
  venueName = "",
  rows = []
}) => {
  return (
    <div className={css.operating_hr_item}>
      <table>
        {showDefault && (
          <tbody>
            <tr>
              <th colSpan="2">Exhibition Galleries</th>
            </tr>
            <tr>
              <td>
                <p>Monday, Tuesday, Wednesday and Thursday</p>
              </td>
              <td>
                <p>9am to 8pm</p>
              </td>
            </tr>
            <tr>
              <td>
                <p>Friday</p>
              </td>
              <td>
                <p>9am to 5pm</p>
              </td>
            </tr>
            <tr>
              <td>
                <p>Weekends</p>
              </td>
              <td>
                <p>10am to 5pm</p>
              </td>
            </tr>
          </tbody>
        )}
        {!showDefault && rows.length > 0 && (
          <tbody>
            <tr>
              <th colSpan="2">{venueName}</th>
            </tr>
            {rows.map(row => (
              <tr key={row.internalKey}>
                <td>
                  <p>{implodeAnd(row.days || [])}</p>
                </td>
                <td>
                  <p>{(row.period || []).join(", ")}</p>
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

OpeningHoursTable.propTypes = {
  showDefault: PropTypes.bool,
  venueName: PropTypes.string,
  rows: PropTypes.array
};

OpeningHoursTable.defaultProps = {
  showDefault: false,
  venueName: "",
  rows: []
};

/**
 * Displays the site footer.
 *
 * Set venueId to 22861 to show opening hours for exhibition galleries.
 */
const Footer = ({ className = "", venueId = 0 }) => {
  const [openingHours, setOpeningHours] = React.useState({});

  const [fetchByUrl, abortFetch] = useAudioGuideApi();

  React.useEffect(() => {
    if (venueId > 0) {
      fetchByUrl(`/ajax/slnsw-data/opening-hours/week/${venueId}`)
        .then(json => setOpeningHours(json))
        .catch(e => {
          /* eslint-disable no-console */
          console.error(e);
          /* eslint-enable no-console */
        });
    }
    return () => {
      abortFetch();
    };
  }, [venueId]);

  const useDefaultOpeningHours = venueId === 0;

  const venueName = openingHours.venue_name || null;
  const locationHoursRows = Object.keys(openingHours.days_of_week || {}).reduce(
    (agg, dayOfWeek, index) => {
      const key = JSON.stringify(openingHours.days_of_week[dayOfWeek]);
      if (index > 0) {
        const { key: previousKey } = agg[agg.length - 1];
        if (previousKey !== key) {
          agg.push({
            internalKey: `${key}_${index}`,
            key,
            period: openingHours.days_of_week[dayOfWeek],
            days: [dayOfWeek]
          });
        } else {
          agg[agg.length - 1].days.push(dayOfWeek);
        }
      } else {
        agg.push({
          internalKey: key,
          key,
          period: openingHours.days_of_week[dayOfWeek],
          days: [dayOfWeek]
        });
      }
      return agg;
    },
    []
  );

  /**
   * Bookmark current page.
   *
   * @see https://gist.github.com/oilvier/70abd45d1f2ffc98b568
   */
  const bookmarkMe = e => {
    if (window.sidebar && window.sidebar.addPanel) {
      // Firefox <23
      e.preventDefault();
      window.sidebar.addPanel(document.title, window.location.href, "");
    } else if (window.external && "AddFavorite" in window.external) {
      // Internet Explorer
      e.preventDefault();
      window.external.AddFavorite(window.location.href, document.title);
    } else if (
      (window.opera && window.print) ||
      (window.sidebar && !(window.sidebar instanceof Node))
    ) {
      // Opera <15 and Firefox >23
      /**
       * For Firefox <23 and Opera <15, no need for JS to add to bookmarks
       * The only thing needed is a `title` and a `rel="sidebar"`
       * To ensure that the bookmarked URL doesn't have a complementary `#` from our trigger's href
       * we force the current URL
       */
      e.target
        .setAttribute("rel", "sidebar")
        .setAttribute("title", document.title)
        .setAttribute("href", window.location.href);
      return true;
    } else {
      /* eslint-disable no-alert */
      // For the other browsers (mainly WebKit) we use a simple alert to inform users that they can add to bookmarks with ctrl+D/cmd+D
      e.preventDefault();
      if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
        alert(
          `You can add this page to your bookmarks by pressing the More icon (box with up arrow) and selecting Add Bookmark.`
        );
      } else if (
        navigator.userAgent.match(/Android|webOS|iPhone|iPod|Blackberry/i)
      ) {
        alert(
          `You can add this page to your bookmarks by pressing the More icon (three vertical dots) and pressing the Star icon.`
        );
      } else {
        alert(
          `You can add this page to your bookmarks by pressing ${
            navigator.userAgent.toLowerCase().indexOf("mac") > -1
              ? "Command/Cmd"
              : "CTRL"
          } + D on your keyboard.`
        );
      }
      /* eslint-enable no-alert */
    }
    // If you have something in the `href` of your trigger.
    return false;
  };

  return (
    <>
      <div className={css.halfCircle}> </div>
      <footer className={[css.footer, className || ""].join(" ")}>
        <div className="text-center">
          <img
            src="/themes/custom/slnsw_audio_guide_theme/slnsw-logo-icon.white.svg"
            alt="State Library of NSW interrobang logo"
            width="28"
          />
        </div>

        <div className={css.info_container}>
          <div className={css.info_item}>
            <h2>State Library of NSW </h2>
          </div>
          <div className={css.info_item}>
            <div className="text-center">
              <a
                className={css.socialLink}
                href="https://www.facebook.com/statelibrarynsw"
                title="State Library of NSW Facebook"
              >
                <img
                  src="/themes/custom/slnsw_audio_guide_theme/facebook-icon.svg"
                  alt="Facebook logo"
                />
              </a>

              <a
                className={css.socialLink}
                href="https://twitter.com/statelibrarynsw"
                title="State Library of NSW Twitter"
              >
                <img
                  src="/themes/custom/slnsw_audio_guide_theme/twitter-icon.svg"
                  alt="Twitter logo"
                />
              </a>

              <a
                className={css.socialLink}
                href="https://instagram.com/statelibrarynsw/"
                title="State Library of NSW Instagram"
              >
                <img
                  src="/themes/custom/slnsw_audio_guide_theme/instagram-icon.svg"
                  alt="Instagram logo"
                />
              </a>

              <button
                className={css.socialLink}
                type="button"
                onClick={bookmarkMe}
              >
                <img
                  src="/themes/custom/slnsw_audio_guide_theme/bookmark-icon.svg"
                  alt="Bookmark icon"
                />
              </button>
            </div>
          </div>
          <div className={css.info_item}>
            <h4>
              <a href="https://www.sl.nsw.gov.au">Library website</a>
            </h4>
          </div>
        </div>

        <div className="css.columnArea">
          <div className="row">
            <div className="col-lg-12">
              <div className={css.operating_hr_container}>
                <Details>
                  <Summary>
                    <h3>Opening hours</h3>
                  </Summary>
                  <OpeningHoursTable
                    showDefault={useDefaultOpeningHours}
                    venueName={venueName}
                    rows={locationHoursRows}
                  />
                  <h4>
                    <a
                      href="https://www.sl.nsw.gov.au/plan-your-visit/opening-hours"
                      target="_blank"
                      rel="noreferrer"
                    >
                      View all opening hours
                    </a>
                  </h4>
                </Details>
              </div>
            </div>
          </div>
        </div>

        {/* Only show on large display */}
        <div className="d-none d-sm-none d-md-none d-lg-block">
          <div className={css.bottomNav}>
            <nav className={css.navItems}>
              <a href="https://www.sl.nsw.gov.au/disclaimer">Disclaimer</a>
              <a href="https://www.sl.nsw.gov.au/privacy">Privacy</a>
              <a href="https://www.sl.nsw.gov.au/copyright">Copyright</a>
              <a href="https://www.sl.nsw.gov.au/right-to-information">
                Right to information
              </a>
              <a href="https://www.sl.nsw.gov.au/website-accessibility">
                Website accessibility
              </a>
            </nav>
          </div>
        </div>
      </footer>
    </>
  );
};

Footer.propTypes = {
  className: PropTypes.string,
  venueId: PropTypes.number
};

Footer.defaultProps = {
  className: "",
  venueId: 0
};

export default Footer;
